import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import canastalLenaImg from '../../../assets/canasta-llena.png';
import canastaVaciaImg from '../../../assets/canasta-vacia.png';
import { Bolsa, CreateBolsaDTO } from '../types';
import { NuevaBolsaModal } from './NuevaBolsaModal';

interface BolsasPanelProps {
  bolsas: Bolsa[];
  selectedBolsaId: string | null;
  loading: boolean;
  error: string | null;
  onSelectBolsa: (bolsaId: string) => void;
  onCreateBolsa: (dto: CreateBolsaDTO) => Promise<void>;
  onDeleteBolsa?: (bolsaId: string) => Promise<void>;
  onEliminarProducto?: (bolsaId: string, lineaId: string) => Promise<void>;
  onSaveAsBorador: (bolsaId: string) => Promise<void>;
  // SPEC-100 (UC-001, RN-004) — abre el modal automáticamente al montar cuando se llega a
  // /catalogo?nuevaBolsa=1 (deep link); no afecta el uso manual del botón.
  // SPEC-138 (RN-005): el Centro de Cotizaciones ya NO es origen de ?nuevaBolsa=1 — su botón
  // "Nueva bolsa" resuelve el destino con resolverDestinoCrear(). El parámetro sigue siendo
  // un deep link válido, pero deja de tener productores en esa pantalla.
  autoOpenNuevaBolsa?: boolean;
}

export function BolsasPanel({
  bolsas,
  selectedBolsaId,
  loading,
  error,
  onSelectBolsa,
  onCreateBolsa,
  onDeleteBolsa,
  onEliminarProducto,
  onSaveAsBorador,
  autoOpenNuevaBolsa,
}: BolsasPanelProps) {
  const navigate = useNavigate();
  const [showNewBolsaDialog, setShowNewBolsaDialog] = useState(false);
  const [bolsaToDelete, setBolsaToDelete] = useState<Bolsa | null>(null);

  useEffect(() => {
    if (autoOpenNuevaBolsa) {
      setShowNewBolsaDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNuevaBolsa]);

  const selectedBolsa = bolsas.find(b => b.id === selectedBolsaId);

  // Bolsa seleccionada siempre al inicio de la lista
  const reorderedBolsas = selectedBolsaId
    ? [
        ...bolsas.filter(b => b.id === selectedBolsaId),
        ...bolsas.filter(b => b.id !== selectedBolsaId),
      ]
    : bolsas;

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* TARJETA 1: BOLSAS ACTIVAS */}
      <Card>
        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography
            variant="h6"
            sx={{
              mb: 1.5,
              fontWeight: 700,
              color: '#7C3E8E',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Bolsas activas
          </Typography>

          {/* Scroll: máximo 3 bolsas antes de scroll interno */}
          {/* SPEC-205 RN-007 — el panel solo recibe bolsas ACTIVA; su estado vacío lo dice. */}
          <Box sx={{ mb: 2, maxHeight: '250px', overflow: 'auto' }}>
            {bolsas.length === 0 ? (
              <Typography sx={{ color: '#6B6B80' }}>No hay bolsas activas</Typography>
            ) : (
              reorderedBolsas.map((bolsa, idx) => (
                <React.Fragment key={bolsa.id}>
                  {idx > 0 && <Divider sx={{ my: 1 }} />}
                  <Box
                    onClick={() => onSelectBolsa(bolsa.id)}
                    sx={{
                      p: 1.5,
                      backgroundColor: selectedBolsaId === bolsa.id ? '#F3E8F7' : 'transparent',
                      borderLeft: selectedBolsaId === bolsa.id ? '3px solid #7C3E8E' : 'none',
                      borderRadius: 0.5,
                      cursor: 'pointer',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start',
                      '&:hover': { backgroundColor: '#F9F3FB' },
                    }}
                  >
                    {/* Ícono canasta */}
                    <Box
                      component="img"
                      data-testid="bolsa-icono"
                      data-estado={bolsa.lineas_count > 0 ? 'llena' : 'vacia'}
                      src={bolsa.lineas_count > 0 ? canastalLenaImg : canastaVaciaImg}
                      alt={bolsa.lineas_count > 0 ? 'Canasta con productos' : 'Canasta vacía'}
                      sx={{ width: 36, height: 36, flexShrink: 0, objectFit: 'contain' }}
                    />

                    {/* Nombre + cantidad */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                        {bolsa.nombre}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#6B6B80' }}>
                        {bolsa.lineas_count} productos
                      </Typography>
                    </Box>

                    {/* Total + botón borrar */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                        ${bolsa.total.toLocaleString('es-CO')}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{ width: 24, height: 24 }}
                        aria-label="Eliminar bolsa"
                        data-testid="btn-eliminar-bolsa"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBolsaToDelete(bolsa);
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 13, color: '#DC3545' }} />
                      </IconButton>
                    </Box>
                  </Box>
                </React.Fragment>
              ))
            )}
          </Box>

          {/* Botón Nueva bolsa */}
          <Button
            fullWidth
            data-testid="btn-nueva-bolsa"
            onClick={() => setShowNewBolsaDialog(true)}
            startIcon={<AddCircleIcon />}
            sx={{
              border: '1.5px dashed #C8A0D8',
              backgroundColor: 'transparent',
              color: '#7C3E8E',
              fontWeight: 500,
              textTransform: 'none',
              py: 1,
            }}
          >
            Nueva bolsa
          </Button>
        </CardContent>
      </Card>

      {/* TARJETA 2: DETALLE DE PRODUCTOS */}
      <Card>
        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography
            variant="h6"
            sx={{
              mb: 1.5,
              fontWeight: 700,
              color: '#7C3E8E',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Detalle de bolsa
          </Typography>

          <Box sx={{ maxHeight: '350px', overflow: 'auto', mb: 2 }}>
            {selectedBolsa && selectedBolsa.lineas && selectedBolsa.lineas.length > 0 ? (
              selectedBolsa.lineas.map((linea) => (
                <Box
                  key={linea.id}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    p: 1.5,
                    mb: 1,
                    backgroundColor: '#F5F5F8',
                    borderRadius: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <Box
                    data-testid="linea-imagen-container"
                    sx={{
                      width: 46, height: 46,
                      backgroundColor: '#E8E8F0',
                      borderRadius: 0.5,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, overflow: 'hidden',
                    }}
                  >
                    {linea.imagen_url ? (
                      <img
                        data-testid={`linea-imagen-${linea.id}`}
                        src={linea.imagen_url}
                        alt={linea.nombre_producto || 'Producto'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <span style={{ fontSize: '1.4rem' }}>📦</span>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {linea.nombre_producto || `[Sin nombre: ${linea.producto_id}]`}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6B6B80' }}>
                      Proveedor
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', textAlign: 'right' }}>
                      ${linea.subtotal.toLocaleString('es-CO')}
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ width: 28, height: 28 }}
                      aria-label="Eliminar producto"
                      data-testid="btn-eliminar-producto"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedBolsa) onEliminarProducto?.(selectedBolsa.id, linea.id);
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14, color: '#DC3545' }} />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography sx={{ color: '#6B6B80' }}>
                {selectedBolsa ? 'No hay productos' : 'Selecciona una bolsa'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* TARJETA 3: RESUMEN Y BOTÓN */}
      {selectedBolsa && (
        <Card>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>Total de la bolsa</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: '#7C3E8E' }}>
                ${selectedBolsa.total.toLocaleString('es-CO')}
              </Typography>
            </Box>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate(`/cotizaciones/detalle/${selectedBolsaId}`)}
              sx={{
                backgroundColor: '#7C3E8E',
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600,
                py: 1.3,
                fontSize: '1rem',
              }}
            >
              Continuar cotización →
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Modal nueva bolsa (SPEC-071) */}
      <NuevaBolsaModal
        open={showNewBolsaDialog}
        onClose={() => setShowNewBolsaDialog(false)}
        onCreateBolsa={onCreateBolsa}
      />

      {/* Diálogo de confirmación para eliminar bolsa */}
      <Dialog open={!!bolsaToDelete} onClose={() => setBolsaToDelete(null)}>
        <DialogTitle>Eliminar bolsa</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de borrar la bolsa "{bolsaToDelete?.nombre}"? Se eliminarán también
            todos sus productos. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBolsaToDelete(null)} data-testid="btn-cancelar-borrar-bolsa">
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            data-testid="btn-confirmar-borrar-bolsa"
            onClick={async () => {
              const target = bolsaToDelete;
              setBolsaToDelete(null);
              if (target) {
                try {
                  await onDeleteBolsa?.(target.id);
                } catch (err) {
                  console.error(err);
                }
              }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
